import { useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { Filename, Spinner } from '@core/components/ui';
import { selectIsGuest } from '@features/auth';
import { selectCurrentProject } from '@features/projects';
import {
  useGetFoldersQuery,
  useGetDocumentsQuery,
  useCreateFolderMutation,
  useRenameFolderMutation,
  useDeleteFolderMutation,
  useUploadDocumentsMutation,
  useMoveDocumentMutation,
  useDuplicateDocumentMutation,
  useDeleteDocumentMutation,
  ConfirmModal,
  DropZone,
  FirstLabelStatus,
  FolderPickerModal,
} from '@features/dataroom';

import { openPreview, openPreviewInCanvas } from '../store/workspaceSlice';

const FILE_ICONS = {
  '.pdf': 'mdi:file-pdf-box',
  '.docx': 'mdi:file-word-box',
  '.doc': 'mdi:file-word-box',
  '.pptx': 'mdi:file-powerpoint-box',
  '.ppt': 'mdi:file-powerpoint-box',
  '.xlsx': 'mdi:file-excel-box',
  '.xls': 'mdi:file-excel-box',
  '.png': 'mdi:file-image-box',
  '.jpg': 'mdi:file-image-box',
  '.jpeg': 'mdi:file-image-box',
  '.txt': 'mdi:file-document-outline',
  '.md': 'mdi:language-markdown',
  '.csv': 'mdi:file-delimited-outline',
  '.zip': 'mdi:folder-zip',
};

const FILE_ICON_COLORS = {
  '.pdf': 'text-red-500',
  '.docx': 'text-blue-600',
  '.doc': 'text-blue-600',
  '.pptx': 'text-orange-500',
  '.ppt': 'text-orange-500',
  '.xlsx': 'text-green-600',
  '.xls': 'text-green-600',
  '.png': 'text-purple-500',
  '.jpg': 'text-purple-500',
  '.jpeg': 'text-purple-500',
  '.md': 'text-gray-600',
  '.csv': 'text-teal-600',
  '.txt': 'text-gray-500',
  '.zip': 'text-amber-600',
};

const STATUS_ICONS = {
  processing: {
    icon: 'mdi:progress-clock',
    color: 'text-amber-500',
    tooltip: 'workspace.fileTree.statusProcessing',
  },
  error: {
    icon: 'mdi:alert-circle',
    color: 'text-red-500',
    tooltip: 'workspace.fileTree.statusError',
  },
};

// Natural sort: "8_…" before "10_…" (matches Finder/Explorer/Drive behavior).
const byNameNatural = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

export function FileTree() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentProject = useAppSelector(selectCurrentProject);
  const isGuest = useAppSelector(selectIsGuest);
  const projectUuid = currentProject?.uuid || null;

  const { data: folders = [], isLoading: foldersLoading } = useGetFoldersQuery(projectUuid, {
    skip: !projectUuid,
  });

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [creatingInParent, setCreatingInParent] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingUuid, setRenamingUuid] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingFolder, setDeletingFolder] = useState(null);

  const [deletingDocument, setDeletingDocument] = useState(null);
  const [movingDocument, setMovingDocument] = useState(null);
  const [duplicatingDocument, setDuplicatingDocument] = useState(null);

  const [createFolder] = useCreateFolderMutation();
  const [renameFolder] = useRenameFolderMutation();
  const [deleteFolder] = useDeleteFolderMutation();
  const [uploadDocuments, { isLoading: isUploading }] = useUploadDocumentsMutation();
  const [moveDocument] = useMoveDocumentMutation();
  const [duplicateDocument] = useDuplicateDocumentMutation();
  const [deleteDocument] = useDeleteDocumentMutation();

  const dropZoneRef = useRef(null);

  const rootFolders = useMemo(
    () => folders.filter((f) => !f.parent_uuid).slice().sort(byNameNatural),
    [folders],
  );
  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const f of folders) {
      const key = f.parent_uuid || '__root__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    for (const list of map.values()) list.sort(byNameNatural);
    return (parentUuid) => map.get(parentUuid) || [];
  }, [folders]);

  const toggleExpanded = (folderUuid) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderUuid)) next.delete(folderUuid);
      else next.add(folderUuid);
      return next;
    });
  };

  const handleCreateSubmit = (parentUuid) => {
    if (newFolderName.trim()) {
      createFolder({ projectUuid, name: newFolderName.trim(), parentUuid });
      setNewFolderName('');
      setCreatingInParent(null);
    }
  };

  const handleRenameSubmit = (folderUuid) => {
    if (renameValue.trim()) {
      renameFolder({ projectUuid, folderUuid, name: renameValue.trim() });
      setRenamingUuid(null);
      setRenameValue('');
    }
  };

  const handleDeleteFolder = (folderUuid) => {
    deleteFolder({ projectUuid, folderUuid });
    setDeletingFolder(null);
  };

  const handleUpload = (files, folderPaths) => {
    uploadDocuments({ projectUuid, folderUuid: null, files, folderPaths });
  };

  const handleMoveDocument = (folderUuid) => {
    if (movingDocument) {
      moveDocument({ projectUuid, documentUuid: movingDocument.uuid, folderUuid });
      setMovingDocument(null);
    }
  };

  const handleDuplicateDocument = () => {
    if (duplicatingDocument) {
      duplicateDocument({ projectUuid, documentUuid: duplicatingDocument.uuid });
      setDuplicatingDocument(null);
    }
  };

  const handleDeleteDocument = () => {
    if (deletingDocument) {
      deleteDocument({ projectUuid, documentUuid: deletingDocument.uuid });
      setDeletingDocument(null);
    }
  };

  if (foldersLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <DropZone ref={dropZoneRef} onUpload={handleUpload} isUploading={isUploading} disabled={isGuest}>
      <div className="text-sm">
        {/* Upload button — guests are view-only, so no write affordances */}
        <div className="flex items-center justify-between px-2 mb-1">
          {!isGuest && (
            <>
              <button
                type="button"
                onClick={() => dropZoneRef.current?.open()}
                className="cursor-pointer flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)] transition-colors"
              >
                <Icon icon="mdi:upload" className="size-3.5" />
                {t('workspace.fileTree.upload')}
              </button>
              <button
                type="button"
                onClick={() => { setCreatingInParent('root'); setNewFolderName(''); }}
                className="cursor-pointer flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)] transition-colors"
              >
                <Icon icon="mdi:folder-plus-outline" className="size-3.5" />
                {t('workspace.fileTree.folder')}
              </button>
            </>
          )}
          <FirstLabelStatus projectUuid={projectUuid} readOnly={isGuest} />
        </div>

        {creatingInParent === 'root' && (
          <form
            onSubmit={(e) => { e.preventDefault(); handleCreateSubmit(null); }}
            className="flex items-center gap-1 py-1 px-2"
          >
            <Icon icon="mdi:folder-plus" className="size-4 text-blue-400 shrink-0" />
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onBlur={() => setCreatingInParent(null)}
              placeholder={t('workspace.fileTree.folderNamePlaceholder')}
              autoFocus
              className="flex-1 text-sm py-0.5 px-1 border border-[var(--color-border)] rounded focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] min-w-0"
            />
          </form>
        )}

        {rootFolders.map((folder) => (
          <FolderNode
            key={folder.uuid}
            folder={folder}
            depth={0}
            readOnly={isGuest}
            projectUuid={projectUuid}
            childrenOf={childrenOf}
            expandedIds={expandedIds}
            toggleExpanded={toggleExpanded}
            renamingUuid={renamingUuid}
            renameValue={renameValue}
            setRenamingUuid={setRenamingUuid}
            setRenameValue={setRenameValue}
            handleRenameSubmit={handleRenameSubmit}
            creatingInParent={creatingInParent}
            setCreatingInParent={setCreatingInParent}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            handleCreateSubmit={handleCreateSubmit}
            setDeletingFolder={setDeletingFolder}
            setExpandedIds={setExpandedIds}
            onFileClick={(doc) => dispatch(openPreview(doc))}
            onFileCanvasClick={(doc) => dispatch(openPreviewInCanvas(doc))}
            setMovingDocument={setMovingDocument}
            setDuplicatingDocument={setDuplicatingDocument}
            setDeletingDocument={setDeletingDocument}
          />
        ))}

        {/* Root-level files */}
        <RootFiles
          projectUuid={projectUuid}
          readOnly={isGuest}
          onFileClick={(doc) => dispatch(openPreview(doc))}
          onFileCanvasClick={(doc) => dispatch(openPreviewInCanvas(doc))}
          setMovingDocument={setMovingDocument}
          setDuplicatingDocument={setDuplicatingDocument}
          setDeletingDocument={setDeletingDocument}
          isEmpty={rootFolders.length === 0}
          onUploadClick={() => dropZoneRef.current?.open()}
        />

        <ConfirmModal
          isOpen={!!deletingFolder}
          onConfirm={() => handleDeleteFolder(deletingFolder?.uuid)}
          onCancel={() => setDeletingFolder(null)}
          title={t('workspace.fileTree.deleteFolderTitle')}
          message={deletingFolder ? t('workspace.fileTree.deleteFolderMessage', { name: deletingFolder.name }) : ''}
          confirmLabel={t('workspace.fileTree.delete')}
          variant="danger"
        />

        <ConfirmModal
          isOpen={!!deletingDocument}
          onConfirm={handleDeleteDocument}
          onCancel={() => setDeletingDocument(null)}
          title={t('workspace.fileTree.deleteDocumentTitle')}
          message={deletingDocument ? t('workspace.fileTree.deleteDocumentMessage', { name: deletingDocument.name }) : ''}
          confirmLabel={t('workspace.fileTree.delete')}
          variant="danger"
        />

        <ConfirmModal
          isOpen={!!duplicatingDocument}
          onConfirm={handleDuplicateDocument}
          onCancel={() => setDuplicatingDocument(null)}
          title={t('workspace.fileTree.duplicateDocumentTitle')}
          message={duplicatingDocument ? t('workspace.fileTree.duplicateDocumentMessage', { name: duplicatingDocument.name }) : ''}
          confirmLabel={t('workspace.fileTree.duplicate')}
          variant="warning"
        />

        {movingDocument && (
          <FolderPickerModal
            isOpen={!!movingDocument}
            onConfirm={handleMoveDocument}
            onCancel={() => setMovingDocument(null)}
            folders={folders}
            currentFolderUuid={movingDocument?.folder_uuid ?? null}
          />
        )}
      </div>
    </DropZone>
  );
}

function RootFiles({
  projectUuid,
  readOnly,
  onFileClick,
  onFileCanvasClick,
  setMovingDocument,
  setDuplicatingDocument,
  setDeletingDocument,
  isEmpty,
  onUploadClick,
}) {
  const { t } = useTranslation();
  const { data: documents = [] } = useGetDocumentsQuery(
    { projectUuid, folderUuid: null },
  );
  const sortedDocs = useMemo(() => documents.slice().sort(byNameNatural), [documents]);

  if (isEmpty && documents.length === 0) {
    if (readOnly) {
      return (
        <div className="w-full py-8 px-4 flex flex-col items-center gap-2 text-[var(--color-text-muted)]">
          <Icon icon="mdi:folder-outline" className="size-8 opacity-50" />
          <span className="text-xs">{t('workspace.fileTree.noDocuments')}</span>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={onUploadClick}
        className="cursor-pointer w-full py-8 px-4 flex flex-col items-center gap-2 text-[var(--color-text-muted)] hover:text-[var(--color-accent)] hover:bg-[var(--color-bg-muted)] rounded-lg transition-colors"
      >
        <Icon icon="mdi:cloud-upload-outline" className="size-8 opacity-50" />
        <span className="text-xs">{t('workspace.fileTree.dropToUpload')}</span>
      </button>
    );
  }

  return sortedDocs.map((doc) => (
    <FileNode
      key={doc.uuid}
      document={doc}
      depth={0}
      projectUuid={projectUuid}
      readOnly={readOnly}
      onFileClick={onFileClick}
      onFileCanvasClick={onFileCanvasClick}
      setMovingDocument={setMovingDocument}
      setDuplicatingDocument={setDuplicatingDocument}
      setDeletingDocument={setDeletingDocument}
    />
  ));
}

function FolderNode({
  folder, depth, projectUuid, readOnly, childrenOf, expandedIds, toggleExpanded,
  renamingUuid, renameValue, setRenamingUuid, setRenameValue, handleRenameSubmit,
  creatingInParent, setCreatingInParent, newFolderName, setNewFolderName, handleCreateSubmit,
  setDeletingFolder, setExpandedIds, onFileClick, onFileCanvasClick,
  setMovingDocument, setDuplicatingDocument, setDeletingDocument,
}) {
  const { t } = useTranslation();
  const children = childrenOf(folder.uuid);
  const isExpanded = expandedIds.has(folder.uuid);
  const isRenaming = renamingUuid === folder.uuid;

  return (
    <div>
      <div
        className="flex items-center gap-1 py-1 px-2 rounded cursor-pointer group hover:bg-[var(--color-bg-muted)] transition-colors"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => toggleExpanded(folder.uuid)}
      >
        <Icon
          icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
          className="size-3.5 text-[var(--color-text-muted)] shrink-0"
        />
        <Icon
          icon={isExpanded ? 'mdi:folder-open' : 'mdi:folder'}
          className="size-4 text-yellow-500 shrink-0"
        />

        {isRenaming ? (
          <form
            onSubmit={(e) => { e.preventDefault(); handleRenameSubmit(folder.uuid); }}
            className="flex-1 min-w-0"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => setRenamingUuid(null)}
              autoFocus
              className="w-full text-sm py-0 px-1 border border-[var(--color-border)] rounded focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)]"
            />
          </form>
        ) : (
          <span className="text-sm truncate flex-1">{folder.name}</span>
        )}

        {!readOnly && <div className="hidden group-hover:flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => {
              setCreatingInParent(folder.uuid);
              setNewFolderName('');
              setExpandedIds((prev) => new Set(prev).add(folder.uuid));
            }}
            className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
            aria-label={t('workspace.fileTree.newSubfolder')}
          >
            <Icon icon="mdi:folder-plus-outline" className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => { setRenamingUuid(folder.uuid); setRenameValue(folder.name); }}
            className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-amber-500"
            aria-label={t('workspace.fileTree.rename')}
          >
            <Icon icon="mdi:pencil-outline" className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => setDeletingFolder(folder)}
            className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
            aria-label={t('workspace.fileTree.delete')}
          >
            <Icon icon="mdi:delete-outline" className="size-3" />
          </button>
        </div>}
      </div>

      {creatingInParent === folder.uuid && (
        <form
          onSubmit={(e) => { e.preventDefault(); handleCreateSubmit(folder.uuid); }}
          className="flex items-center gap-1 py-1 px-2"
          style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}
        >
          <Icon icon="mdi:folder-plus" className="size-4 text-blue-400 shrink-0" />
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onBlur={() => setCreatingInParent(null)}
            placeholder={t('workspace.fileTree.folderNamePlaceholder')}
            autoFocus
            className="flex-1 text-sm py-0.5 px-1 border border-[var(--color-border)] rounded focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] min-w-0"
          />
        </form>
      )}

      {isExpanded && (
        <>
          {children.map((child) => (
            <FolderNode
              key={child.uuid}
              folder={child}
              depth={depth + 1}
              projectUuid={projectUuid}
              readOnly={readOnly}
              childrenOf={childrenOf}
              expandedIds={expandedIds}
              toggleExpanded={toggleExpanded}
              renamingUuid={renamingUuid}
              renameValue={renameValue}
              setRenamingUuid={setRenamingUuid}
              setRenameValue={setRenameValue}
              handleRenameSubmit={handleRenameSubmit}
              creatingInParent={creatingInParent}
              setCreatingInParent={setCreatingInParent}
              newFolderName={newFolderName}
              setNewFolderName={setNewFolderName}
              handleCreateSubmit={handleCreateSubmit}
              setDeletingFolder={setDeletingFolder}
              setExpandedIds={setExpandedIds}
              onFileClick={onFileClick}
              onFileCanvasClick={onFileCanvasClick}
              setMovingDocument={setMovingDocument}
              setDuplicatingDocument={setDuplicatingDocument}
              setDeletingDocument={setDeletingDocument}
            />
          ))}
          <FolderFiles
            projectUuid={projectUuid}
            folderUuid={folder.uuid}
            depth={depth + 1}
            readOnly={readOnly}
            onFileClick={onFileClick}
            onFileCanvasClick={onFileCanvasClick}
            setMovingDocument={setMovingDocument}
            setDuplicatingDocument={setDuplicatingDocument}
            setDeletingDocument={setDeletingDocument}
          />
        </>
      )}
    </div>
  );
}

function FolderFiles({
  projectUuid, folderUuid, depth, readOnly, onFileClick, onFileCanvasClick,
  setMovingDocument, setDuplicatingDocument, setDeletingDocument,
}) {
  const { data: documents = [] } = useGetDocumentsQuery(
    { projectUuid, folderUuid },
    { skip: !folderUuid },
  );
  const sortedDocs = useMemo(() => documents.slice().sort(byNameNatural), [documents]);

  return sortedDocs.map((doc) => (
    <FileNode
      key={doc.uuid}
      document={doc}
      depth={depth}
      projectUuid={projectUuid}
      readOnly={readOnly}
      onFileClick={onFileClick}
      onFileCanvasClick={onFileCanvasClick}
      setMovingDocument={setMovingDocument}
      setDuplicatingDocument={setDuplicatingDocument}
      setDeletingDocument={setDeletingDocument}
    />
  ));
}

function FileNode({
  document: doc,
  depth,
  readOnly,
  onFileClick,
  onFileCanvasClick,
  setMovingDocument,
  setDuplicatingDocument,
  setDeletingDocument,
}) {
  const { t } = useTranslation();
  const icon = FILE_ICONS[doc.file_type] || 'mdi:file-outline';
  const iconColor = FILE_ICON_COLORS[doc.file_type] || 'text-gray-400';
  const statusInfo = STATUS_ICONS[doc.status];

  return (
    <div
      className="flex items-center gap-1.5 py-1 px-2 rounded hover:bg-[var(--color-bg-muted)] transition-colors group"
      style={{ paddingLeft: `${depth * 16 + 24}px` }}
    >
      <button
        type="button"
        onClick={() => onFileClick(doc)}
        className="cursor-pointer flex items-center gap-1.5 flex-1 min-w-0 text-left"
      >
        <Icon icon={icon} className={`size-4 shrink-0 ${iconColor}`} />
        <Filename name={doc.name} className="text-sm flex-1 text-[var(--color-text)]" />
        {statusInfo && (
          <span title={t(statusInfo.tooltip)} className="shrink-0 inline-flex">
            <Icon icon={statusInfo.icon} className={`size-3.5 ${statusInfo.color}`} />
          </span>
        )}
      </button>
      <div className="hidden group-hover:flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onFileCanvasClick(doc)}
          className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
          aria-label={t('workspace.fileTree.openInCanvas')}
          title={t('workspace.fileTree.openInCanvas')}
        >
          <Icon icon="mdi:dock-right" className="size-3" />
        </button>
        {/* Open in canvas stays for guests; the write actions do not. */}
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={() => setMovingDocument(doc)}
              className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
              aria-label={t('workspace.fileTree.move')}
            >
              <Icon icon="mdi:folder-move-outline" className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => setDuplicatingDocument(doc)}
              className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-amber-500"
              aria-label={t('workspace.fileTree.duplicate')}
            >
              <Icon icon="mdi:content-copy" className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => setDeletingDocument(doc)}
              className="cursor-pointer p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
              aria-label={t('workspace.fileTree.delete')}
            >
              <Icon icon="mdi:delete-outline" className="size-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
